import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useNavigate } from "react-router-dom";
import PartnerListComponent from "./component";

const PartnerListContainer = ({ getDataFromAPI }) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [allPartners, setAllPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Fetch partners with optional search query
  const fetchPartners = useCallback(async (page = 0, isLoadMore = false, searchQuery = "") => {
    if (isLoadMore && (loadMoreLoading || !hasMore)) return;
    if (!isLoadMore && (loading || pageNumber !== page)) return;

    const isSearching = !!searchQuery.trim();
    
    if (isLoadMore) {
      setLoadMoreLoading(true);
    } else if (isSearching) {
      setSearchLoading(true);
    } else {
      setLoading(true);
    }
    
    try {
      const params = new URLSearchParams({
        pageNumber: page,
        pageSize: pageSize
      });

      // Add search parameter if provided
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await new Promise((resolve, reject) => {
        getDataFromAPI(
          `/dashboard/api/v2/admin/franchisee?${params.toString()}`,
          "GET",
          undefined,
          resolve,
          reject
        );
      });

      if (page === 0) {
        setAllPartners(response);
        setFilteredPartners(response);
      } else {
        setAllPartners(prev => [...prev, ...response]);
        setFilteredPartners(prev => [...prev, ...response]);
      }

      setHasMore(response.length === pageSize);
      setPageNumber(page);
      setSearchMode(isSearching);
    } catch (err) {
      console.error("Partner API Error:", err);
    } finally {
      if (isLoadMore) {
        setLoadMoreLoading(false);
      } else if (isSearching) {
        setSearchLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [pageNumber, hasMore, loading, loadMoreLoading, getDataFromAPI, pageSize]);

  // Initial load
  useEffect(() => {
    fetchPartners(0, false);
  }, []);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue.trim() === "") {
        // If search is cleared, reset to initial data
        if (searchMode) {
          fetchPartners(0, false, "");
        }
      } else {
        // Trigger API search with search term
        fetchPartners(0, false, searchValue);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Local filtering for better UX (optional, removes if API handles all search)
  const filterPartnersLocally = (searchTerm) => {
    if (searchTerm.trim() === "") {
      return allPartners;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return allPartners.filter(partner => {
      return (
        (partner.id?.toString().toLowerCase().includes(searchLower)) ||
        (partner.name?.toLowerCase().includes(searchLower)) ||
        (partner.cohortId?.toString().toLowerCase().includes(searchLower)) ||
        (partner.ownerId?.toString().toLowerCase().includes(searchLower))
      );
    });
  };

  const handleBlock = () => {
    navigate(`/home/configuration/partner/block-payout`);
  };

  const handleUnblock = () => {
    navigate(`/home/configuration/partner/block-payout-list`);
  };

  const handleLoadMore = () => {
    if (hasMore && !loadMoreLoading) {
      if (searchMode) {
        // Continue search pagination
        fetchPartners(pageNumber + 1, true, searchValue);
      } else {
        // Normal pagination
        fetchPartners(pageNumber + 1, true);
      }
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  // Show loading state if we're doing initial search
  const isLoading = loading || searchLoading;
  const showLoadMore = hasMore && !searchMode && !searchLoading;

  return (
    <PartnerListComponent
      partners={filteredPartners}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      onBlock={handleBlock}
      onUnblock={handleUnblock}
      onSelectPartner={setSelectedPartnerId}
      selectedPartnerId={selectedPartnerId}
      onLoadMore={handleLoadMore}
      hasMore={showLoadMore}
      loading={loadMoreLoading}
      searchLoading={searchLoading}
      onGoBack={handleGoBack}
      searchMode={searchMode}
    />
  );
};

export default connect(null, { getDataFromAPI })(PartnerListContainer);